/**
 * Vista pública de un link compartido (SDD §7.5): solo lectura, sin sesión,
 * sin analítica; `noindex` y `no-referrer` también los pone el hosting.
 */
import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { templateName } from '../lib/notes'
import { useApp } from '../components/AppContext'
import { NotesView } from '../components/NotesPanel'
import { Brand, ErrorBox, Skeleton } from '../components/ui'
import { EcoApiError } from '../lib/api'
import { formatDate, formatDuration } from '../lib/format'
import { useAsync, useDocumentTitle } from '../lib/hooks'
import { segmentAt } from '../lib/transcript'

export function PublicShare() {
  const { token = '' } = useParams()
  const { api } = useApp()
  const share = useAsync(() => api.publicShare(token), [api, token])
  useDocumentTitle(share.data?.titulo)

  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    const ref = document.createElement('meta')
    ref.name = 'referrer'
    ref.content = 'no-referrer'
    document.head.append(meta, ref)
    return () => {
      meta.remove()
      ref.remove()
    }
  }, [])

  const s = share.data
  // `?t=ms`: link a un momento de la transcripción (los tiempos de cada línea son links)
  const [params] = useSearchParams()
  const t = Number(params.get('t'))
  const [marcado, setMarcado] = useState<number | null>(null)
  useEffect(() => {
    if (!s?.transcripcion?.length || !Number.isFinite(t) || t <= 0) return
    const i = segmentAt(s.transcripcion, t)
    if (i < 0) return
    setMarcado(i)
    document.getElementById(`seg-${i}`)?.scrollIntoView({ block: 'center' })
  }, [s, t])

  return (
    <div className="public">
      <div className="public-head">
        <Brand to="/login" />
        <span className="faint" style={{ fontSize: 12 }}>
          Compartido con eco
        </span>
      </div>
      <div className="page narrow">
        {share.error &&
        !(share.error instanceof EcoApiError && [404, 410].includes(share.error.status)) ? (
          <ErrorBox error={share.error} onRetry={() => void share.reload()} />
        ) : share.error ? (
          <div className="empty">
            <h2>Este link no está disponible</h2>
            <p className="muted">
              Puede que haya vencido o que quien lo compartió lo haya desactivado.
            </p>
          </div>
        ) : !s ? (
          <div className="stack">
            <Skeleton h={34} w="60%" />
            <Skeleton h={200} />
          </div>
        ) : (
          <div className="stack">
            <div>
              <h1>{s.titulo}</h1>
              <p className="muted" style={{ marginTop: 4 }}>
                {formatDate(s.fechaInicio)}
                {s.hablantes.length > 0 && ` · ${s.hablantes.join(', ')}`}
              </p>
            </div>
            {s.notas.map((n) => (
              <div key={`${n.templateId}-${n.version}`} className="card">
                <h2 style={{ marginBottom: 12 }}>{templateName(n)}</h2>
                <NotesView
                  templateId={n.templateId}
                  contenido={n.contenido}
                  nameOf={(e) => s.nombres?.[e] ?? e}
                />
              </div>
            ))}
            {s.transcripcion && (
              <div className="card">
                <h2 style={{ marginBottom: 12 }}>Transcripción</h2>
                {s.transcripcion.map((seg, i) => {
                  const prev = s.transcripcion![i - 1]
                  return (
                    <div
                      key={i}
                      id={`seg-${i}`}
                      className={`seg ${prev?.speaker === seg.speaker && marcado !== i ? 'cont' : ''} ${marcado === i ? 'flash' : ''}`}
                      style={{ paddingLeft: 0 }}
                    >
                      {(prev?.speaker !== seg.speaker || marcado === i) && (
                        <div>
                          <span className="who">{seg.speaker}</span>
                          <Link
                            className="ts"
                            to={{ search: `?t=${seg.inicioMs}` }}
                            replace
                            title="Link a este momento"
                          >
                            {formatDuration(seg.inicioMs)}
                          </Link>
                        </div>
                      )}
                      <div className="tx">{seg.texto}</div>
                    </div>
                  )
                })}
              </div>
            )}
            <p className="faint" style={{ fontSize: 12 }}>
              Grabado con eco. El organizador declaró haber avisado a los participantes:{' '}
              {s.consentimientoDeclarado ? 'sí' : 'no'}.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
